/*
 * assets/js/src/sneh.js — sníh a hvězdička v hrdinovi rozcestníku (dev.16.17).
 *
 * Rozhodnutí majitele 25. 9. 2026 „A + D" nad návrhem
 * `zimni-carousel-navrh/hero-navrh/hero-navrh.html?v=A&sneh=1`. Logika je
 * z inline skriptu návrhu 1:1 (generátor převzatý z bublin prezentace
 * Mýdlového workshopu, `bubbles(n)`), jen jako modul téhož balíku.
 *
 * Běží jen na `.chero[data-za-sneh]` — atribut sází šablona archivu štítku,
 * když má rozcestník zapnutý „Vizuál v hlavičce". Ostatní stránky modul
 * nenajde a nic neudělá.
 *
 * ---------------------------------------------------------------------
 * CO DĚLÁ
 * ---------------------------------------------------------------------
 *  1. Sníh: vrstva `.snih` jako první dítě hrdiny, 28 vloček (mobil 20),
 *     náhodná pozice, velikost 4–10 px, trvání 11–21 s, záporné zpoždění,
 *     opacita 0,35–0,6. Pohyb je čisté CSS (`za-snih`), JS jen jednou
 *     vygeneruje vločky — žádná smyčka.
 *  2. Hvězdička: každých 6–12 s blýskne buď na špičce stromku (`.tipspark`),
 *     nebo na náhodném místě pozadí hrdiny, NIKDY přes text ani vizuál
 *     (drobečky, slova H1, datum, perex, figure + 24 px okraj). Nejvýš dvě
 *     současně; ve skryté záložce a mimo výřez se nespouští.
 *  3. Parallax stromku: proměnná `--p` na `<figure>` podle posunu (max 600).
 *
 * Perex (`.lead`) je v seznamu míst, kam hvězdička nesmí, NAVÍC proti
 * návrhu: návrh ho v seznamu neměl, ale pravidlo zadání zní „vyhýbá se
 * textu" a perex pod H1 přibyl až s dev.16.17.
 *
 * `prefers-reduced-motion: reduce` = modul nevytvoří nic (sníh ani záblesky)
 * a parallax nepoběží; stromek v CSS stojí.
 *
 * Proměnné vloček se nastavují přes `style.setProperty` (CSSOM), ne přes
 * `style="…"` v `innerHTML` — funguje i pod přísnou CSP.
 */

const SEL = '.chero[data-za-sneh]';
const POCET = 28;
const POCET_MOBIL = 20;
const STAR = '<svg viewBox="-10 -10 20 20" aria-hidden="true" focusable="false"><path d="M0-10Q0 0 10 0Q0 0 0 10Q0 0-10 0Q0 0 0-10Z" fill="#fff"/><path d="M0-5Q0 0 5 0Q0 0 0 5Q0 0-5 0Q0 0 0-5Z" fill="#BFEFF6" transform="rotate(45)"/><circle r="1.4" fill="#fff"/></svg>';

/**
 * Vrstva sněhu jako první dítě hrdiny (pod `.wrap`, který má `z-index: 2`).
 *
 * @param {Element} hero Kořen `.chero`.
 * @param {number} n Počet vloček.
 */
function snih(hero, n) {
	const vrstva = document.createElement('div');
	vrstva.className = 'snih';
	vrstva.setAttribute('aria-hidden', 'true');
	for (let i = 0; i < n; i += 1) {
		const vl = document.createElement('span');
		vl.className = 'vl';
		vl.style.setProperty('--x', `${(2 + Math.random() * 96).toFixed(1)}%`);
		vl.style.setProperty('--s', `${(4 + Math.random() * 6).toFixed(1)}px`);
		vl.style.setProperty('--d', `${(11 + Math.random() * 10).toFixed(1)}s`);
		vl.style.setProperty('--dl', `${(-Math.random() * 16).toFixed(1)}s`);
		vl.style.setProperty('--o', (0.35 + Math.random() * 0.25).toFixed(2));
		vrstva.appendChild(vl);
	}
	hero.prepend(vrstva);
}

/**
 * Náhodné místo pro záblesk mimo text a vizuál, nebo `null`.
 *
 * @param {Element} hero Kořen `.chero`.
 * @param {Element|null} fig Vizuál ve slotu.
 * @returns {number[]|null} [x, y] vůči hrdinovi.
 */
function misto(hero, fig) {
	const hr = hero.getBoundingClientRect();
	const prekazky = Array.prototype.slice
		.call(hero.querySelectorAll('.crumb li, h1 .w, .hubdatum time, .lead'))
		.concat(fig ? [fig] : [])
		.map((el) => el.getBoundingClientRect());
	for (let k = 0; k < 25; k += 1) {
		const x = 16 + Math.random() * (hr.width - 32);
		const y = 16 + Math.random() * (hr.height - 32);
		const X = hr.left + x;
		const Y = hr.top + y;
		const koliduje = prekazky.some((r) => X > r.left - 24 && X < r.right + 24 && Y > r.top - 24 && Y < r.bottom + 24);
		if (!koliduje) {
			return [x, y];
		}
	}
	return null;
}

/**
 * Nastaví sníh, záblesky a parallax pro jeden hrdinu.
 *
 * @param {Element} hero Kořen `.chero[data-za-sneh]`.
 */
function setup(hero) {
	const mobil = window.matchMedia('(max-width: 767px)').matches;
	const fig = hero.querySelector('.hubvis');
	const tip = fig ? fig.querySelector('.tipspark') : null;

	snih(hero, mobil ? POCET_MOBIL : POCET);

	const jiskry = document.createElement('div');
	jiskry.className = 'sparks';
	jiskry.setAttribute('aria-hidden', 'true');
	hero.prepend(jiskry);

	const blesk = (naSpicku) => {
		if (document.hidden || hero.getBoundingClientRect().bottom < 0) {
			return;
		}
		if (naSpicku && tip) {
			tip.classList.remove('go');
			tip.getBoundingClientRect();
			tip.classList.add('go');
			return;
		}
		if (jiskry.children.length >= 2) {
			return;
		}
		const p = misto(hero, fig);
		if (!p) {
			return;
		}
		const e = document.createElement('span');
		e.className = 'spk';
		e.style.left = `${p[0]}px`;
		e.style.top = `${p[1]}px`;
		e.innerHTML = STAR;
		jiskry.appendChild(e);
		window.setTimeout(() => e.remove(), 1300);
	};
	const smycka = () => {
		blesk(Boolean(tip) && Math.random() < 0.4);
		window.setTimeout(smycka, 6000 + Math.random() * 6000);
	};
	window.setTimeout(smycka, 2500 + Math.random() * 3000);

	if (fig) {
		let ceka = false;
		window.addEventListener(
			'scroll',
			() => {
				if (ceka) {
					return;
				}
				ceka = true;
				window.requestAnimationFrame(() => {
					fig.style.setProperty('--p', String(Math.min(window.scrollY, 600)));
					ceka = false;
				});
			},
			{ passive: true }
		);
	}
}

/**
 * Spustí modul. Vrací počet hrdinů se sněhem (kontrakt ostatních modulů).
 *
 * @returns {number}
 */
export function init() {
	const heroes = document.querySelectorAll(SEL);
	if (!heroes.length) {
		return 0;
	}
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return 0;
	}
	let count = 0;
	for (const hero of heroes) {
		if (hero.dataset.zaSnehInit === '1') {
			continue;
		}
		hero.dataset.zaSnehInit = '1';
		setup(hero);
		count += 1;
	}
	return count;
}
